import { ReactNode, useEffect, useState } from "react";
import { FormWrapper } from "../FormWrapper";
import StoreIcon from "@mui/icons-material/Store";
import Api from "../../api/Api";
import { Availability, Staff, Store, StoreForReservation } from "../../api/ApiData";
import { Button, FormControl, MenuItem, Select, Stack, Typography } from "@mui/material";
import Space from "../Space";
import TimeTable from "../TimeTable/TimeTable";
import FormLabel from "../FormLabel";
import { ReservationState } from "../../util/useReservationState";
import useAsyncUpdate from "../../util/useAsyncUpdate";
import TagManager from "react-gtm-module";
import infoIcon from "../../img/icon_info.png";
import { StyledText } from "../StyledText";
import { Person } from "@mui/icons-material";
import { Action, Func, VoidAction } from "../../util/func";
import { palette } from "../../theme";
import getFormParams from "./getFormParams";
import { useApiContext } from "../../api/ApiProvider";
import useFiles from "../../util/useFile";

export default function Form1({ state }: { state: ReservationState }) {
  const params = getFormParams();
  const { api } = useApiContext();
  const [stores] = useAsyncUpdate(() => api.GetStoresForReservation(params.storeIds, params.courseIds));
  const [availability, setAvailability] = useState<Availability | null>();
  const [paging, setPaging] = useState<number>(0);

  useEffect(() => {
    if (state.data.store) {
      (window as any).dataLayer = [];
      if (state.data.store?.google_tag_manager_id !== "") {
        TagManager.initialize({ gtmId: state.data.store.google_tag_manager_id });
      }
    }
  }, [state.data.store?.id]);

  useEffect(() => {
    setAvailability(undefined);
    if (state.data.store && state.data.course) {
      if (state.data.staff?.unavailable_courses?.includes(state.data.course.id)) {
        state.setValue("course", undefined);
      } else {
        api.GetAvailability(paging, state.data.store, state.data.staff?.id, state.data.course.id).then(setAvailability);
      }
    }
  }, [state.data.staff, state.data.course, paging]);

  useEffect(() => {
    const date = availability?.find((t) => t === params.reservationStartTime);
    if (date) {
      state.setValue("date", date);
    }
  }, [availability]);

  if (!stores) {
    return <></>;
  }

  return (
    <FormWrapper currentIndex={0} skipConfirm={state.data.store?.skip_confirm}>
      <Space h={10} />
      <Stack style={{ gap: 20 }}>
        <StyledSelect label="ご希望の店舗" items={stores} value={state.data.store} setValue={(store) => state.setValue("store", store)} getExplanation={(store) => store.message} errorMessage="利用可能な店舗がありません" locked={stores.length === 1} />
        {state.data.store && (
          <StaffSelect
            store={state.data.store}
            value={state.data.staff}
            setValue={(staff) => {
              state.setValue("staff", staff);
              if (state.data.course && staff?.unavailable_courses.includes(state.data.course.id)) {
                state.setValue("course", undefined);
              }
            }}
          />
        )}
        {state.data.store && (
          <StyledSelect
            label="ご希望のメニュー"
            items={state.data.store.courses.filter((c) => {
              if (state.data.staff !== undefined) {
                return !state.data.staff.unavailable_courses.includes(c.id);
              } else {
                return true;
              }
            })}
            value={state.data.course}
            setValue={(course) => state.setValue("course", course)}
            getExplanation={(course) => course.explanation}
            errorMessage="利用可能なメニューがありません"
            locked={state.data.store.courses.length === 1}
          />
        )}
        {(() => {
          if (state.data.store && state.data.course && availability) {
            return (
              <Stack>
                <FormLabel required icon={<StoreIcon />} text="ご希望の日時" />
                <Space h={10} />
                <TimeTable page={paging} store={state.data.store} availability={availability} onPaging={(page) => setPaging(page)} onSelected={(date) => state.setValue("date", date)} />
              </Stack>
            );
          }
        })()}
      </Stack>
      <Space h={30} />
    </FormWrapper>
  );
}

function StaffButton({ label, imageUrl, selected, showImage, onclick }: { label: string; imageUrl: string | undefined; selected: boolean; showImage: boolean; onclick: VoidAction }) {
  let img: ReactNode;
  if (showImage) {
    if (imageUrl) {
      img = <img src={imageUrl} alt={imageUrl} style={{ width: 90, height: 90, objectFit: "cover" }} />;
    } else {
      img = <Person color="disabled" sx={{ width: 75, height: 90 }} />;
    }
  } else {
    img = <></>;
  }

  return (
    <Button variant="contained" onClick={onclick} sx={{ minWidth: "auto", p: 1, border: `2px solid ${selected ? palette.primary.main : "lightgray"}` }} color={selected ? "selectedButton" : "unselectedButton"}>
      <Stack alignItems="center" gap={1}>
        <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: 90, fontSize: ".8rem" }}>{label}</Typography>
        {img}
      </Stack>
    </Button>
  );
}

function StyledSelect<T extends { id: number; name: string }>({ label, items, value, setValue, getExplanation, errorMessage, locked }: { label: string; items: T[]; value: T | undefined; setValue: Action<T | undefined>; getExplanation: Func<T, string>; errorMessage: string; locked: boolean }) {
  useEffect(() => {
    if (locked && items && items.length === 1 && items[0].id !== value?.id) {
      setValue(items[0]);
    }
  });

  return (
    <Stack>
      <FormLabel required icon={<StoreIcon />} text={label} />
      <Space h={10} />
      <FormControl fullWidth>
        <Select
          value={value?.id?.toString() ?? ""}
          onChange={(e) => setValue(items.find((item) => item.id.toString() === e.target.value.toString()))}
          disabled={locked || items.length === 0}
          sx={{ "& .MuiInputBase-input": { padding: "13px 14px" } }}
          displayEmpty
          renderValue={(selected) => {
            let text;
            if (items.length === 0) {
              text = errorMessage;
            } else {
              text = items.find((item) => item.id.toString() === selected.toString())?.name ?? "";
            }
            return (
              <Typography whiteSpace="normal" fontSize="0.8rem">
                {text}
              </Typography>
            );
          }}
        >
          {items.map((item) => (
            <MenuItem key={item.id} value={item.id} sx={{ whiteSpace: "normal" }}>
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Space h={10} />
      {(() => {
        if (value !== undefined && getExplanation(value) !== "") {
          const explanation = getExplanation(value);
          if (explanation !== "") {
            return (
              <Stack direction={"row"} alignItems={"flex-start"} gap={1}>
                <img width={20} height={20} style={{ flexShrink: 0, flexGrow: 0, marginTop: "1px" }} src={infoIcon} />
                <StyledText textStyle={"StoreMessage"}>{explanation}</StyledText>
              </Stack>
            );
          }
        }
      })()}
    </Stack>
  );
}
function StaffSelect({ store, value, setValue }: { store: StoreForReservation; value: Staff | undefined; setValue: Action<Staff | undefined> }) {
  const { api } = useApiContext();
  const { fileUriDict } = useFiles(
    store.staffs.filter((s) => s.file_uuid).map((s) => s.file_uuid!),
    api,
    [store]
  );
  if (!store.nomination_enabled || !fileUriDict) {
    return <></>;
  }

  const showImage = store.staffs.some((s) => s.file_uuid !== null);
  return (
    <Stack gap={1}>
      <FormLabel icon={<StoreIcon />} text="ご希望のスタッフ" />
      <Stack direction="row" gap={1} sx={{ overflowX: "auto", scrollbarWidth: "thin" }} whiteSpace="nowrap">
        <StaffButton label="指名なし" imageUrl={undefined} onclick={() => setValue(undefined)} selected={value === undefined} showImage={showImage} />
        {store.staffs.map((s) => (
          <StaffButton key={s.id} label={s.nominee_name} imageUrl={s.file_uuid ? fileUriDict[s.file_uuid] : undefined} onclick={() => setValue(s)} selected={value?.id === s.id} showImage={showImage} />
        ))}
      </Stack>
      <StaffDetail staff={value} imageUrl={value?.file_uuid ? fileUriDict[value.file_uuid] : undefined} />
    </Stack>
  );
}

function StaffDetail({ staff, imageUrl }: { staff: Staff | undefined; imageUrl: string | undefined }) {
  if (staff === undefined) {
    return <></>;
  }

  let img = <></>;
  if (staff.file_uuid) {
    img = <img src={imageUrl} alt="" style={{ maxWidth: "25vw", height: 164, width: 164, objectFit: "contain", flexShrink: 0 }} />;
  }
  return (
    <Stack direction="row" alignItems="center">
      {img}
      <Stack p={1} flexGrow={1} color={"gray"}>
        <Typography fontSize={20} color={"black"}>
          {staff.nominee_name}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography>{staff.nominee_role}</Typography>
          <Stack direction="row" alignItems="center">
            <Typography>指名料:</Typography>
            <Typography color="red" fontSize={20}>
              ￥{staff.nominee_price.toLocaleString()}
            </Typography>
          </Stack>
        </Stack>
        <Stack pt={1} visibility={staff.nominee_message !== "" ? "visible" : "hidden"}>
          <Typography>スタッフからの一言</Typography>
          <Typography>{staff.nominee_message}</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}
